import type { ReactNode } from "react";
import { AuroraBackground } from "@/components/aurora-background";
import { SectionEyebrow } from "@/components/section-eyebrow";

export type ContentPageProps = {
  eyebrow: string;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
};

/**
 * Shared layout for editorial/listing pages (blog, case studies, legal).
 * Renders an aurora hero with eyebrow + headline + lede, then the page body.
 */
export function ContentPage({ eyebrow, title, description, children }: ContentPageProps) {
  return (
    <article className="text-white">
      <section className="relative px-6 pt-20 pb-12 overflow-hidden">
        <AuroraBackground />
        <div className="relative max-w-5xl mx-auto">
          <SectionEyebrow>{eyebrow}</SectionEyebrow>
          <h1 className="text-4xl md:text-6xl font-semibold tracking-tight mb-6 text-balance">
            {title}
          </h1>
          {description ? (
            <p className="text-lg md:text-xl text-white/75 max-w-3xl">{description}</p>
          ) : null}
        </div>
      </section>

      {children}
    </article>
  );
}

/** Section helper for long-form prose inside ContentPage. */
export function ContentProse({ children }: { children: ReactNode }) {
  return (
    <section className="px-6 pb-24">
      <div className="max-w-3xl mx-auto prose prose-invert prose-headings:tracking-tight prose-headings:text-white prose-p:text-white/75 prose-a:text-pink-soft prose-strong:text-white prose-li:text-white/75">
        {children}
      </div>
    </section>
  );
}
